import React from 'react';
import { createTss } from 'tss-react';

type ColorTheme = {
  bg: {
    default: string;
    brand100: string;
    brand0: string;
  };
  border: {
    default: string;
    disabled: string;
    error: string;
    focused: string;
    hovered: string;
    subtle: string;
  };
  container: {
    default: string;
    subtle: string;
  };
  fill: {
    default: string;
    disabled: string;
    hovered: string;
  };
  icon: {
    default: string;
  },
  scroll: {
    thumb: string;
    track: string;
  };
  text: {
    default: string;
    disabled: string;
    subtle: string;
    subtlest: string;
  };
};

type BreakPoint = 'xs' | 'sm' | 'md' | 'lg';

type Theme = {
  breakpoints: Record <BreakPoint, number> & {
    above: (breakpoint: BreakPoint) => string;
    below: (breakpoint: BreakPoint) => string;
  };
  color: ColorTheme;
  duration: {
    normal: string;
  };
  spacing: (n: number) => number;
};

type TssContext = {
  theme: Theme;
};

const ThemeBase: Omit<Theme, 'color'> = {
  breakpoints: {
    above: (breakpoint: BreakPoint) => `@media screen and (min-width: ${ThemeBase.breakpoints[breakpoint]}px)`,
    below: (breakpoint: BreakPoint) => `@media screen and (max-width: ${ThemeBase.breakpoints[breakpoint]}px)`,
    xs: 480,
    sm: 600,
    md: 1024,
    lg: 1280,
  },
  duration: {
    normal: '200ms',
  },
  spacing: (n: number) => n * 8,
};

const LightTheme: ColorTheme = {
  bg: {
    default: '#f0f1f3',
    brand100: '#1d4b87',
    brand0: '#407bb7',
  },
  border: {
    default: '#c3c6cf',
    disabled: '#e1e2e6',
    error: '#d54146',
    focused: '#4b71db',
    hovered: '#74777f',
    subtle: '#e1e2e6',
  },
  container: {
    default: '#ffffff',
    subtle: 'rgba(0, 0, 0, 0.1)',
  },
  fill: {
    default: '#f0f1f3',
    disabled: '#f0f1f3',
    hovered: '#e1e2e6',
  },
  icon: {
    default: '#0f2256',
  },
  scroll: {
    thumb: '#a6abb7',
    track: '#f0f1f3',
  },
  text: {
    default: '#0b1940',
    disabled: '#a6abb7',
    subtle: '#505258',
    subtlest: '#8c919b',
  },
};

const DarkTheme: ColorTheme = {
  bg: {
    default: '#1b1b1d',
    brand100: '#1b1b1d',
    brand0: '#283352',
  },
  border: {
    default: '#505258',
    disabled: '#45474c',
    error: '#ac3135',
    focused: '#6f8ee3',
    hovered: '#c3c6cf',
    subtle: '#45474c',
  },
  container: {
    default: '#252628',
    subtle: 'rgba(0, 0, 0, 0.2)',
  },
  fill: {
    default: '#2f3034',
    disabled: '#2f3034',
    hovered: '#3a3b3f',
  },
  icon: {
    default: '#ffffff',
  },
  scroll: {
    thumb: '#5c5e65',
    track: '#2f3034',
  },
  text: {
    default: '#edf1fb',
    disabled: '#5c5e65',
    subtle: '#a6abb7',
    subtlest: '#74777f',
  },
};

const useContext = (): TssContext => {
  // istanbul ignore next -- @preserve
  const colorThemeQuery = 'matchMedia' in window ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;
  const [colorTheme, setColorTheme] = React.useState<ColorTheme>(
    // istanbul ignore next -- @preserve
    colorThemeQuery?.matches ? DarkTheme : LightTheme,
  );

  React.useEffect(() => {
    const listener = (e: MediaQueryListEvent) => {
      // istanbul ignore next -- @preserve
      setColorTheme(e.matches ? DarkTheme : LightTheme);
    };
    colorThemeQuery?.addEventListener('change', listener);
    return () => colorThemeQuery?.removeEventListener('change', listener);
  }, []);

  return {
    theme: { ...ThemeBase, color: colorTheme },
  };
};

const { tss } = createTss({ useContext });
export const createStyles = tss.withNestedSelectors().create;
